import { Formik } from 'formik';
import keycode from 'keycode';
import Checkbox from '@mui/material/Checkbox';
import EmailIcon from '@mui/icons-material/Email';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import * as Yup from 'yup';

import CardText from 'components/Card/Text';
import CardTitle from 'components/Card/Title';
import auth from 'lib/auth';
import tracker from 'lib/tracker';

import FormControlLabel from '@mui/material/FormControlLabel';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import SocialButtons from './SocialButtons';

const getLocation = () => window.location;

const Register = ({ loading, onEmail, onLoading, onLogin, onStateChange }) => {
  const [error, setError] = useState(null);
  const {
    pathname,
    search,
  } = getLocation();
  return (
    <Formik
      initialValues={useMemo(
        () => ({
          email: '',
          password: '',
          terms: false,
        }),
        [],
      )}
      onSubmit={useCallback(
        async ({ email, password }, { setSubmitting }) => {
          onEmail(email);
          onLoading(true);
          const redirectUrl = ((pathname || '').indexOf('sign-in') !== -1) ? '' : `${pathname}${search}`;
          const { err, message, state } = await auth.register(email, password, encodeURIComponent(redirectUrl));
          if (err) {
            tracker.trackGoogleAnalyticsException(message, { email });
            setError(message);
            onLoading(false);
          } else if (state) {
            tracker.trackGoogleAnalyticsEvent('User', 'Register');
            onStateChange(state);
          } else {
            onLoading(false);
          }
          setSubmitting(false);
        },
        [
          onEmail,
          onLoading,
          onStateChange,
          pathname,
          search,
        ],
      )}
      validationSchema={useMemo(
        () => Yup
          .object()
          .shape({
            email: Yup.string()
              .trim()
              .lowercase()
              .email('Email must be valid')
              .required('Email is required'),
            password: Yup.string()
              .trim()
              .min(8, 'Password must be at least 8 characters long')
              .required('Password is required'),
            terms: Yup.boolean()
              .oneOf([true], 'You must agree to continue'),
          }),
        [],
      )}
    >
      {useCallback(
        ({
          dirty,
          errors,
          isSubmitting,
          isValid,
          setFieldTouched,
          setFieldValue,
          submitForm,
          touched,
          values,
        }) => {
          const busy = isSubmitting || loading;
          const canSubmit = !busy && dirty && isValid;
          return (
            <>
              <CardTitle title='Sign Up' />
              <SocialButtons label='Sign up' />
              <CardText noTopPadding>
                <TextField
                  disabled={busy}
                  errorText={touched?.email && errors?.email}
                  label='Email'
                  fullWidth
                  onBlur={() => setFieldTouched('email')}
                  onChange={(e, value) => {
                    setError(null);
                    setFieldValue('email', value);
                  }}
                  onKeyUp={e => {
                    if (canSubmit) {
                      const key = keycode(e);
                      if (key === 'enter' || key === 'return') {
                        submitForm();
                      }
                    }
                  }}
                  value={values.email}
                />
                <TextField
                  disabled={busy}
                  helperText={touched?.password && errors?.password}
                  error={touched?.password && errors?.password}
                  label='Password'
                  type='password'
                  fullWidth
                  onBlur={() => setFieldTouched('password')}
                  onChange={(e, value) => {
                    setError(null);
                    setFieldValue('password', value);
                  }}
                  onKeyUp={e => {
                    if (canSubmit) {
                      const key = keycode(e);
                      if (key === 'enter' || key === 'return') {
                        submitForm();
                      }
                    }
                  }}
                  value={values.password}
                />
              </CardText>
              <CardText noTopPadding>
                <FormControlLabel
                  label='I agree to the Terms of Service'
                  disabled={isSubmitting}
                  control={(
                    <Checkbox
                      error={errors?.terms}
                      name='terms'
                      defaultChecked={false}
                      onChange={(e) => {
                        setFieldTouched('terms');
                        setFieldValue('terms', e.target.checked);
                      }}
                      value={values.terms}
                    />
                  )}
                />
              </CardText>
              <CardText noTopPadding>
                <SecondaryButton
                  label='Already have an account?'
                  onClick={onLogin}
                />
              </CardText>
              <PrimaryButton
                disabled={!canSubmit}
                error={error}
                fullWidth
                icon={<EmailIcon />}
                label='Send registration email'
                loading={busy}
                onClick={submitForm}
              />
            </>
          );
        },
        [
          error,
          loading,
          onLogin,
        ],
      )}
    </Formik>
  );
};

Register.propTypes = {
  loading: PropTypes.bool,
  onEmail: PropTypes.func.isRequired,
  onLoading: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onStateChange: PropTypes.func.isRequired,
};

export default Register;
